import { productImageAlt } from "@/lib/imageAlt";
import { useMemo, useState } from "react";
import { useProductRating } from "@/hooks/useProductRating";
import { StarRating } from "@/components/StarRating";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getShopifyImageUrl, formatPrice, ShopifyProduct } from "@/lib/shopify";
import { getDiscountPercent, genuineOriginalPrice } from "@/lib/pricing";
import { FREE_SHIPPING_THRESHOLD, DELIVERY_WINDOW_COPY, SHIPPING_REGION_COPY } from "@/lib/shipping";
import { useCartStore } from "@/stores/cartStore";
import { startPartnerCheckout } from "@/lib/checkout";
import { useWishlistStore } from "@/stores/wishlistStore";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Loader2, Minus, Plus, Heart, Truck, Shield, Star } from "lucide-react";
import { toast } from "sonner";
import { RoomVisualizer } from "./RoomVisualizer";
import { PaymentPartnerNote } from "./PaymentPartnerNote";
import { DimsCard } from "./DimsCard";
import { PolicyAccordion } from "./PolicyAccordion";

import { SocialShare } from "./SocialShare";

import { PriceDropAlert } from "./PriceDropAlert";
import { LowStockWarning } from "./LowStockWarning";
import { CompleteTheLook } from "./CompleteTheLook";
import { BackInStockAlert } from "./BackInStockAlert";





// Color name to CSS color mapping for swatches
const COLOR_MAP: Record<string, string> = {
  walnut: "#5C3A21", oak: "#C4A35A", white: "#F5F5F5", black: "#1A1A1A",
  gray: "#9CA3AF", grey: "#9CA3AF", brown: "#8B4513", natural: "#D2B48C",
  cream: "#FFFDD0", ivory: "#FFFFF0", beige: "#F5F5DC", tan: "#D2B48C",
  gold: "#D4AF37", silver: "#C0C0C0", brass: "#B5A642", copper: "#B87333",
  bronze: "#CD7F32", navy: "#001F3F", blue: "#3B82F6", red: "#EF4444",
  green: "#22C55E", teal: "#14B8A6", pink: "#EC4899", orange: "#F97316",
  yellow: "#EAB308", purple: "#A855F7", charcoal: "#36454F", espresso: "#3C2415",
  mahogany: "#C04000", cherry: "#DE3163", maple: "#C5924A", pine: "#4A7C59",
  birch: "#F2E8CF", ash: "#B2BEB5", slate: "#708090", smoke: "#738276",
};

const isColorOption = (name: string) => ["color", "colour", "wood stain", "finish", "shade"].some(n => name.toLowerCase().includes(n));
const isMaterialOption = (name: string) => ["material", "fabric", "upholstery", "textile", "leather"].some(n => name.toLowerCase().includes(n));
const isSizeOption = (name: string) => ["size", "dimension", "seating", "configuration", "length", "width"].some(n => name.toLowerCase().includes(n));
const getSwatchColor = (value: string): string | null => {
  const lower = value.toLowerCase().trim();
  for (const [key, color] of Object.entries(COLOR_MAP)) {
    if (lower.includes(key)) return color;
  }
  return null;
};

// Title/description cleaning lives in @/lib/productTitle so grids, rails and the
// PDP all share one implementation (and the uniqueness guard).
import { cleanProductTitle, cleanProductDescription, extractSizeLabel, parseTitleAttributes, truncateAtSentence } from "@/lib/productTitle";
import { areDimensionsPlausible, extractOverallDims, titleAgreesWithDims } from "@/lib/dimensions";
import { isLargeFurniture } from "@/lib/productSize";
import { optionValueAvailable, variantForOption, variantLabel } from "@/lib/variants";

export { cleanProductTitle, cleanProductDescription };



interface ProductInfoPanelProps {
  product: ShopifyProduct['node'];
  /** On-page name, identical to the prerendered H1 (productPageName). */
  pageName?: string;
  selectedVariant: string | null;
  setSelectedVariant: (id: string) => void;
  quantity: number;
  setQuantity: (fn: (q: number) => number) => void;
  recommendations: ShopifyProduct[];
  /**
   * Whether the supplier's measurements describe the selected variant
   * (sizeFactsApply). When not, only a size the variant states itself is shown.
   */
  sizeFactsApply?: boolean;
}

export const ProductInfoPanel = ({
  product, pageName, selectedVariant, setSelectedVariant, quantity, setQuantity, recommendations, sizeFactsApply = true,
}: ProductInfoPanelProps) => {
  const shortName = cleanProductTitle(product.title, product.productType);
  const name = pageName || shortName;
  
  const addItem = useCartStore(state => state.addItem);
  const isCartLoading = useCartStore(state => state.isLoading);
  const toggleWishlist = useWishlistStore(state => state.toggle);
  const isWishlisted = useWishlistStore(state => state.has(product.handle));
  const numericId = product.id?.replace(/^gid:\/\/shopify\/Product\//, "") || "";
  const { rating: productRating, count: reviewCount } = useProductRating(numericId, product.handle);

  const variants = product.variants?.edges || [];
  const currentVariant = variants.find(v => v.node.id === selectedVariant)?.node;
  const optionGroups = product.options?.filter(o => o.name !== "Title" || o.values.length > 1) || [];

  // U.S. window: 4–10 business days including processing (site-policy.mjs).
  const deliveryDates = useMemo(() => {
    const addBusinessDays = (from: Date, n: number) => {
      const d = new Date(from);
      while (n > 0) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) n--;
      }
      return d;
    };
    const now = new Date();
    const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(addBusinessDays(now, 4))} – ${fmt(addBusinessDays(now, 10))}`;
  }, []);

  // What went into the cart, by the option picked: "Ivory Area Rug, 6' X 9'",
  // not the size in the product's title ("8' x 11' Ivory Area Rug").
  const addedName = (variant: (typeof variants)[number]["node"]) => {
    const picked = variantLabel(product, variant);
    if (!picked) return shortName;
    const titleSize = extractSizeLabel(product.title);
    const name = titleSize
      ? shortName.replace(new RegExp(titleSize.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i"), "").replace(/\s{2,}/g, " ").trim()
      : shortName;
    return `${name || shortName}, ${picked}`;
  };

  // Full cleaned description (vendor names + spec dumps removed).
  const fullDescription = useMemo(
    () => (product?.description ? cleanProductDescription(product.description) : ""),
    [product?.description]
  );

  // Short summary near the title — cut at a sentence boundary, never mid-word.
  const shortDescription = useMemo(() => truncateAtSentence(fullDescription, 200), [fullDescription]);


  const handleAddToCart = async () => {
    if (!product || !selectedVariant) return;
    const variant = variants.find(v => v.node.id === selectedVariant);
    if (!variant) return;
    const result = await addItem({
      product: { node: product },
      variantId: variant.node.id,
      variantTitle: variant.node.title,
      price: variant.node.price,
      quantity,
      selectedOptions: variant.node.selectedOptions || [],
    });
    // Shopify can add fewer than asked, or none; the cart store has then said so itself.
    if (result.ok && !result.notice) {
      toast.success("Added to cart", { description: `${quantity}x ${addedName(variant.node)}`, position: "top-center" });
    }
  };

  const handleBuyNow = async () => {
    if (!product || !selectedVariant) return;
    const variant = variants.find(v => v.node.id === selectedVariant);
    if (!variant) return;
    const result = await addItem({
      product: { node: product },
      variantId: variant.node.id,
      variantTitle: variant.node.title,
      price: variant.node.price,
      quantity,
      selectedOptions: variant.node.selectedOptions || [],
    });
    if (!result.ok) return;
    // Fewer than asked for: open the cart, which says so on the line, rather
    // than a checkout that quietly holds a different quantity.
    if (result.notice) {
      window.location.assign("/cart");
      return;
    }
    const url = useCartStore.getState().getCheckoutUrl();
    if (url) await startPartnerCheckout('buy_now');
    else toast.error("Couldn't start checkout — please try again.", { position: "top-center" });
  };

  const handleToggleWishlist = () => {
    toggleWishlist(product.handle);
    toast.success(isWishlisted ? "Removed from wishlist" : "Added to wishlist", {
      description: shortName, position: "top-center",
    });
  };

  const scrollToReviews = () => {
    document.getElementById("pdp-reviews")?.scrollIntoView({ behavior: "smooth" });
  };

  // A $0.00 price is a broken supplier feed row, not a giveaway. 659 such
  // products were live and purchasable. Listings now exclude them, but anyone
  // holding a direct link must not be able to order one either.
  const isSellable = parseFloat(currentVariant?.price?.amount || "0") > 0;

  if (currentVariant && !isSellable) {
    return (
      <motion.div className="lg:pt-0" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
        <h1 className="font-display text-[30px] md:text-[38px] leading-[1.1] font-normal tracking-[-0.01em] text-foreground mb-3">
          {name}
        </h1>
        <p className="text-[15px] font-medium text-foreground mb-2">
          This piece isn't currently available to order.
        </p>
        <p className="text-[14px] text-muted-foreground leading-relaxed mb-6">
          We don't have a confirmed price for it from our supply partner, so we can't
          sell it. Browse the rest of the collection, or email{" "}
          <a href="mailto:hello@loftloom.com" className="text-primary underline">hello@loftloom.com</a>{" "}
          and we'll look into it.
        </p>
        {shortDescription && (
          <p className="text-[14px] text-muted-foreground leading-relaxed">{shortDescription}</p>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div className="lg:pt-0" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
      {/* Title */}
      <h1 className="font-display text-[30px] md:text-[38px] leading-[1.1] font-normal tracking-normal break-words text-foreground mb-2">
        {name}
      </h1>

      {/* Spec summary — real data only, each value skipped when missing/implausible */}
      {(() => {
        const attrs = parseTitleAttributes(product.title);
        const parts: string[] = [];
        if (attrs.pieceCount) parts.push(attrs.pieceCount);
        if (attrs.configs[0]) parts.push(attrs.configs[0]);
        const dims = extractOverallDims(sizeFactsApply ? product.description : undefined, currentVariant?.title, product.title, product.productType);
        const dimsOk =
          !!dims?.Width && areDimensionsPlausible(dims, product.title) && (!sizeFactsApply || titleAgreesWithDims(product.title, dims));
        if (dimsOk) parts.push(`${dims!.Width} wide`);
        return (
          <>
            {parts.length > 0 && (
              <p className="text-[13px] text-muted-foreground mb-3">{parts.join(" · ")}</p>
            )}
            {dimsOk && <DimsCard dims={dims!} />}
          </>
        );
      })()}

      {/* Short summary — sentence-boundary excerpt; full copy lives in Details */}
      {shortDescription && (
        <p className="text-[14px] text-muted-foreground leading-relaxed mb-4">
          {shortDescription}
        </p>
      )}



      {/* Price */}
      {currentVariant && (() => {
        const currentPriceNum = parseFloat(currentVariant.price.amount);
        const originalPrice = genuineOriginalPrice(currentPriceNum, currentVariant.compareAtPrice?.amount, currentVariant.availableForSale);
        const discountPct = originalPrice ? getDiscountPercent(originalPrice, currentPriceNum) : 0;
        const showSale = originalPrice && originalPrice > currentPriceNum;

        return (
          <div className="mb-1">
            {showSale && (
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[15px] text-muted-foreground line-through">
                  {formatPrice(originalPrice.toString(), currentVariant.price.currencyCode)}
                </span>
                <span className="text-[11px] font-medium tracking-[0.1em] uppercase text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                  {discountPct}% OFF
                </span>
              </div>
            )}
            <span className={`text-[22px] font-medium tabular-nums ${showSale ? "text-primary" : "text-foreground"}`}>
              {formatPrice(currentVariant.price.amount, currentVariant.price.currencyCode)}
            </span>
          </div>
        );
      })()}

      {/* Free delivery badge */}
      {currentVariant && parseFloat(currentVariant.price.amount) >= FREE_SHIPPING_THRESHOLD && (
        <span className="inline-block text-[11px] tracking-[0.1em] uppercase font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full mb-1.5">
          Free U.S. Delivery
        </span>
      )}

      {/* Financing note — full breakdown lives in the inline calculator below */}

      {/* Star Rating — clickable to scroll to reviews */}
      {productRating !== null && productRating > 0 ? (
        <button onClick={scrollToReviews} className="flex items-center gap-2 mb-6 group">
          <StarRating rating={productRating} count={reviewCount} size={16} />
          <span className="text-[13px] text-muted-foreground group-hover:underline">See reviews</span>
        </button>
      ) : (
        <div className="mb-6" />
      )}
      {/* Social proof */}
      <div className="mb-4">
        
      </div>

      <div className="border-t border-border mb-6" />

      {/* In Stock indicator */}
      {currentVariant?.availableForSale && (
        <div className="flex items-start gap-2 mb-3">
          <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-[5px]" />
          <span className="text-[13px] text-primary font-medium leading-snug">In Stock — {DELIVERY_WINDOW_COPY}</span>
        </div>
      )}

      {/* Low stock urgency */}
      {currentVariant?.availableForSale && (
        <div className="mb-5">
          <LowStockWarning />
        </div>
      )}

      {/* Option selectors */}
      {optionGroups.map((option) => {
        const isColor = isColorOption(option.name);
        // Colour dots only when every value gets its own colour: "Blue" and
        // "Blue, Orange" were two identical blue dots. Otherwise text pills.
        const dots = isColor ? option.values.map(getSwatchColor) : [];
        const useDots = dots.length > 0 && dots.every(Boolean) && new Set(dots).size === dots.length;
        return (
          <div key={option.name} className="mb-6">
            <div className="flex items-baseline gap-1.5 mb-3">
              <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-foreground">{option.name}</span>
              <span className="text-[14px] text-muted-foreground">
                {currentVariant?.selectedOptions?.find(o => o.name === option.name)?.value}
              </span>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {/* One button per value. Picking a value keeps the other options
                  when that combination is in stock (variantForOption); a value
                  is only disabled when no in-stock variant has it. */}
              {option.values.map((optValue) => {
                const target = variantForOption(variants.map((v) => v.node), currentVariant, option.name, optValue);
                if (!target) return null;
                const available = optionValueAvailable(variants.map((v) => v.node), option.name, optValue);
                const isSelected = currentVariant?.selectedOptions?.find(o => o.name === option.name)?.value === optValue;
                const swatchColor = useDots ? getSwatchColor(optValue) : null;
                const select = () => {
                  setSelectedVariant(target.id);
                  // The value picked is not sold with the other options as they
                  // were (no Blue in 5' x 8'): say what changed with it.
                  const moved = (target.selectedOptions || []).filter(
                    (o) => o.name !== option.name && currentVariant && o.value !== currentVariant.selectedOptions?.find((c) => c.name === o.name)?.value,
                  );
                  if (moved.length) {
                    toast.info(`${optValue} comes in ${moved.map((o) => o.value).join(", ")}`, {
                      description: `${moved.map((o) => o.name).join(" and ")} changed to match.`,
                      position: "top-center",
                    });
                  }
                };

                if (swatchColor) {
                  return (
                    <button key={optValue} onClick={select} disabled={!available} title={optValue}
                      aria-label={`${option.name}: ${optValue}`}
                      aria-pressed={isSelected}
                      className={`w-9 h-9 rounded-full transition-all ${isSelected ? 'ring-2 ring-foreground ring-offset-2' : available ? 'hover:ring-2 hover:ring-foreground/30 hover:ring-offset-1' : 'opacity-30 cursor-not-allowed'}`}
                      style={{ backgroundColor: swatchColor }}
                    />
                  );
                }

                if (isMaterialOption(option.name)) {
                  return (
                    <button key={optValue} onClick={select} disabled={!available} aria-pressed={isSelected}
                      className={`h-11 min-w-[60px] px-5 rounded-full border text-[13px] transition-all flex items-center gap-2 ${isSelected ? 'border-primary text-primary font-semibold bg-primary/5' : available ? 'border-border text-foreground hover:border-foreground/40' : 'border-border opacity-30 cursor-not-allowed line-through'}`}>
                      <span>{optValue}</span>
                      {target.price && <span className="text-[11px] text-muted-foreground">{formatPrice(target.price.amount, target.price.currencyCode)}</span>}
                    </button>
                  );
                }

                if (isSizeOption(option.name)) {
                  return (
                    <button key={optValue} onClick={select} disabled={!available} aria-pressed={isSelected}
                      className={`h-11 min-w-[48px] px-5 rounded-full border text-[13px] font-medium transition-all ${isSelected ? 'border-foreground text-foreground bg-foreground/5 font-bold' : available ? 'border-border text-foreground hover:border-foreground/40' : 'border-border opacity-30 cursor-not-allowed line-through'}`}>
                      {optValue}
                    </button>
                  );
                }

                return (
                  <button key={optValue} onClick={select} disabled={!available} aria-pressed={isSelected}
                    className={`h-10 min-w-[40px] px-4 rounded-full border text-[13px] transition-all ${isSelected ? 'border-primary text-primary font-semibold bg-primary/5' : available ? 'border-border text-foreground hover:border-foreground/40' : 'border-border opacity-30 cursor-not-allowed line-through'}`}>
                    {optValue}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Unavailable notice + Back in Stock Alert */}
      {!currentVariant?.availableForSale && (
        <div className="mb-6 space-y-3">
          {/* A page whose titled size is sold out opens on it: say that the others are not. */}
          <p className="text-destructive text-sm font-medium">
            {currentVariant && variants.some((v) => v.node.availableForSale)
              ? `${variantLabel(product, currentVariant) || "This option"} is sold out. Choose another option above; the ones not crossed out are in stock.`
              : "This variant is currently unavailable"}
          </p>
          <BackInStockAlert productHandle={product.handle} productTitle={shortName} />
        </div>
      )}

      {/* Quantity + Add to Cart */}
      {currentVariant?.availableForSale && (
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center border border-border rounded-full h-[52px]">
          <button aria-label="Decrease quantity" onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-11 h-full flex items-center justify-center hover:bg-secondary/50 rounded-l-full transition-all duration-150 active:scale-95">
            <Minus className="h-3.5 w-3.5 text-foreground/60" />
          </button>
          <span aria-live="polite" aria-label={`Quantity ${quantity}`} className="w-8 text-center text-[14px] font-semibold text-foreground select-none">{quantity}</span>
          <button aria-label="Increase quantity" onClick={() => setQuantity(q => q + 1)} className="w-11 h-full flex items-center justify-center hover:bg-secondary/50 rounded-r-full transition-all duration-150 active:scale-95">
            <Plus className="h-3.5 w-3.5 text-foreground/60" />
          </button>
        </div>
        <Button onClick={handleAddToCart} variant="purchase" size="lg" data-pdp-add-to-cart
          className="flex-1 h-[52px]"
          disabled={isCartLoading}>
          {isCartLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          ADD TO CART
        </Button>
        <button aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"} aria-pressed={isWishlisted} onClick={handleToggleWishlist} className="flex items-center justify-center w-[52px] h-[52px] hover:opacity-70 transition-all duration-150 active:scale-90 flex-shrink-0">
          <Heart className={`h-6 w-6 transition-all duration-200 ${isWishlisted ? "fill-destructive text-destructive scale-110" : "text-foreground/60"}`} />
        </button>
      </div>
      )}

      {currentVariant?.availableForSale && (
        <div className="mb-5">
          <Button onClick={handleBuyNow} variant="outline" size="lg" disabled={isCartLoading}
            className="w-full rounded-full h-[52px] text-[12px] tracking-[0.18em] uppercase font-medium border-foreground text-foreground hover:bg-foreground hover:text-background">
            Buy it now
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-1.5 mt-3" aria-label="Accepted cards">
            {["Visa", "Mastercard", "Amex", "Discover", "PayPal", "SSL encrypted"].map((b) => (
              <span key={b} className="rounded-full border border-border bg-background px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-foreground/80">{b}</span>
            ))}
          </div>
          <PaymentPartnerNote />
        </div>
      )}

      {/* Room Visualizer — large furniture only; pointless for a table lamp. */}
      {isLargeFurniture(product.productType, product.title) && product.images?.edges?.[0]?.node?.url && (
        <div className="mb-5">
          <RoomVisualizer
            productName={shortName}
            productImageUrl={product.images.edges[0].node.url}
          />
          <p className="text-[11px] text-muted-foreground mt-2 text-center">
            Upload a photo of your room to preview this piece in place.
          </p>
        </div>
      )}



      {/* Social Share & Price Alert */}
      <div className="flex items-center justify-between mb-5">
        <SocialShare
          productTitle={shortName}
          productUrl={`https://loftloom.com/product/${product.handle}`}
          productImage={product.images?.edges?.[0]?.node?.url}
        />
        <PriceDropAlert
          productHandle={product.handle}
          productTitle={shortName}
          currentPrice={parseFloat(currentVariant?.price?.amount || "0")}
        />
      </div>

      {/* Delivery — the fulfilment partner's stated window. No ZIP guessing:
          a date derived from the first digit of a ZIP is not an estimate. */}
      <div className="bg-sand/40 rounded-2xl p-5 mt-5">
        <p className="text-[11px] font-medium text-foreground uppercase tracking-[0.18em] mb-2">Delivery</p>
        {currentVariant?.availableForSale && (
          <p className="text-[14px] text-foreground mb-1.5">
            Order today, estimated U.S. delivery <span className="font-semibold">{deliveryDates}</span>
          </p>
        )}
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          {DELIVERY_WINDOW_COPY} — our fulfilment partner's estimate. {SHIPPING_REGION_COPY}
        </p>
      </div>

      {/* Damage cover — same terms as /returns */}
      <div className="rounded-2xl border border-border p-5 mt-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Shield className="h-[18px] w-[18px] text-primary flex-shrink-0" />
          <p className="text-[14px] font-semibold text-foreground">If it arrives damaged, we make it right</p>
        </div>
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          Tell us within 48 hours of delivery, with photos. We cover return shipping and arrange a replacement or refund.{" "}
          <Link to="/returns" className="underline underline-offset-2 hover:text-foreground">Returns policy</Link>
        </p>
      </div>

      {/* Trust */}
      <div className="space-y-3 mt-4 mb-4">
        <div className="flex items-center gap-3 text-[14px]">
          <Truck className="h-[18px] w-[18px] text-foreground/40 flex-shrink-0" />
          <span className="text-foreground">30-day returns.</span>
        </div>
      </div>
      <PolicyAccordion />


      {/* Complete the Look bundle */}
      <CompleteTheLook currentProduct={product} currentVariant={currentVariant} recommendations={recommendations} />

      {/* Accordions removed — content moved to ProductDetails section */}
    </motion.div>
  );
};
